<template>
  <div class="faq max-w-4xl mx-auto">
    <div class="glass-card p-8">
      <!-- 헤더 -->
      <div class="text-center mb-8">
        <div class="text-4xl mb-3">❓</div>
        <h1 class="text-3xl font-bold text-gradient mb-2">자주 묻는 질문 (FAQ)</h1>
        <p class="text-gray-600">로또 지니어스에 대한 모든 궁금증을 해결해드립니다</p>
      </div>

      <!-- 검색 -->
      <div class="mb-8">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="질문을 검색하세요..."
          class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <!-- 카테고리 필터 -->
      <div class="flex flex-wrap gap-2 mb-8">
        <button
          v-for="cat in categories"
          :key="cat.id"
          @click="selectedCategory = cat.id"
          :class="[
            'px-4 py-2 rounded-lg font-semibold transition-all',
            selectedCategory === cat.id
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          ]"
        >
          {{ cat.icon }} {{ cat.name }}
        </button>
      </div>

      <!-- FAQ 목록 -->
      <div class="space-y-3">
        <details
          v-for="faq in filteredFAQs"
          :key="faq.id"
          class="glass-card p-5 cursor-pointer group"
        >
          <summary class="font-bold text-lg list-none flex items-start justify-between">
            <span class="flex items-start flex-1">
              <span class="text-2xl mr-3 flex-shrink-0">{{ faq.icon }}</span>
              <span class="group-hover:text-blue-600 transition-colors">{{ faq.question }}</span>
            </span>
            <span class="text-gray-400 group-open:rotate-180 transition-transform ml-2">▼</span>
          </summary>
          <div class="mt-4 pl-11 text-gray-700 space-y-3">
            <p v-for="(para, idx) in faq.answer" :key="idx">{{ para }}</p>
            <div v-if="faq.important" class="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
              <p class="text-sm text-yellow-800">
                <strong>💡 중요:</strong> {{ faq.important }}
              </p>
            </div>
          </div>
        </details>
      </div>

      <!-- 추가 질문 -->
      <div class="mt-12 bg-blue-50 p-6 rounded-xl text-center">
        <h3 class="font-bold text-lg mb-2">찾으시는 답변이 없나요?</h3>
        <p class="text-gray-600 mb-4">문의하기 페이지에서 직접 질문해 주세요!</p>
        <button
          @click="$emit('navigate', 'contact')"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          문의하기 →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const emit = defineEmits(['navigate']);

const searchQuery = ref('');
const selectedCategory = ref('all');

const categories = ref([
  { id: 'all', name: '전체', icon: '📋' },
  { id: 'service', name: '서비스', icon: '🎯' },
  { id: 'probability', name: '확률', icon: '🎲' },
  { id: 'ai', name: 'AI', icon: '🤖' },
  { id: 'data', name: '데이터', icon: '📊' },
  { id: 'privacy', name: '개인정보', icon: '🔒' }
]);

const faqs = ref([
  // 서비스 관련
  {
    id: 1,
    category: 'service',
    icon: '🎪',
    question: 'AI가 정말 당첨 확률을 높여주나요?',
    answer: [
      '아니요. 모든 로또 번호 조합의 당첨 확률은 정확히 1/8,145,060로 동일합니다.',
      'AI, 패턴, 통계 분석 등 어떤 방법을 사용하더라도 당첨 확률을 높일 수는 없습니다.',
      '로또 지니어스의 AI와 패턴 생성기는 순전히 재미있는 번호 선택 경험을 제공하기 위한 엔터테인먼트 기능입니다.'
    ],
    important: '이 서비스는 당첨을 약속하거나 확률을 개선하지 않습니다. 순전히 재미를 위한 도구입니다.'
  },
  {
    id: 2,
    category: 'service',
    icon: '💰',
    question: '서비스 이용료가 있나요?',
    answer: [
      '로또 지니어스는 완전 무료입니다.',
      '회원가입도 필요 없으며, 숨겨진 비용이나 구독료가 전혀 없습니다.',
      '광고 수익으로 운영되는 무료 서비스입니다.'
    ]
  },
  {
    id: 3,
    category: 'service',
    icon: '📱',
    question: '모바일에서도 사용할 수 있나요?',
    answer: [
      '네! 로또 지니어스는 반응형 디자인으로 제작되어 스마트폰, 태블릿, PC 등 모든 기기에서 최적화된 경험을 제공합니다.',
      '별도의 앱 설치 없이 웹브라우저에서 바로 이용하실 수 있습니다.'
    ]
  },

  // 확률 관련
  {
    id: 4,
    category: 'probability',
    icon: '🎲',
    question: '1,2,3,4,5,6은 절대 안 나오는 거 아닌가요?',
    answer: [
      '아니요! 1,2,3,4,5,6 조합도 다른 모든 조합과 정확히 같은 1/8,145,060의 확률을 가집니다.',
      '사람들이 "있을 것 같지 않다"고 느끼는 것은 심리적 편향일 뿐이며, 수학적으로는 모든 조합이 동일한 확률입니다.',
      '실제로 세계 여러 나라에서 연속 번호 조합이 당첨된 사례도 있습니다.'
    ],
    important: '직관과 확률은 다릅니다. 모든 조합이 동등하게 "있을 법하거나" "없을 법합니다".'
  },
  {
    id: 5,
    category: 'probability',
    icon: '📈',
    question: '자주 나온 번호(핫넘버)가 또 나올 확률이 높나요?',
    answer: [
      '아니요. 이것은 "도박사의 오류"라고 불리는 흔한 착각입니다.',
      '각 추첨은 완전히 독립적인 사건이므로, 과거 결과는 미래에 영향을 주지 않습니다.',
      '핫넘버든 콜드넘버든 다음 추첨에서 나올 확률은 정확히 동일합니다.'
    ],
    important: '과거 데이터는 재미있는 통계일 뿐, 미래를 예측하는 도구가 아닙니다.'
  },
  {
    id: 6,
    category: 'probability',
    icon: '💸',
    question: '많이 사면 당첨 확률이 높아지나요?',
    answer: [
      '1장당 확률은 동일하지만, 100장을 사면 100번의 독립적인 기회를 얻는 것은 맞습니다.',
      '하지만 100장을 사도 당첨 확률은 약 0.00123%로 여전히 매우 낮습니다.',
      '경제적으로는 평균적으로 구입 금액의 약 50%를 잃게 됩니다.',
      '재정에 무리가 가지 않는 선에서 즐기는 것이 중요합니다.'
    ]
  },

  // AI 관련
  {
    id: 7,
    category: 'ai',
    icon: '🤖',
    question: 'AI는 어떻게 번호를 생성하나요?',
    answer: [
      '5가지 머신러닝 모델(LSTM, GAN, Transformer, CNN, VAE)을 사용하여 과거 데이터에서 다양한 패턴을 학습합니다.',
      '하지만 이는 순전히 재미를 위한 것으로, 당첨 확률과는 무관합니다.',
      '각 AI 캐릭터는 고유한 성격과 알고리즘을 가지고 있어 다양한 번호 조합을 생성합니다.'
    ]
  },
  {
    id: 8,
    category: 'ai',
    icon: '🎨',
    question: 'AI마다 당첨 확률이 다른가요?',
    answer: [
      '아니요. 어떤 AI를 사용하든 생성된 번호의 당첨 확률은 모두 1/8,145,060로 동일합니다.',
      'AI의 차이는 단지 번호를 선택하는 "방식"의 차이일 뿐, 결과의 확률에는 영향을 주지 않습니다.',
      '재미있는 경험을 위해 다양한 AI를 시도해보세요!'
    ]
  },

  // 데이터 관련
  {
    id: 9,
    category: 'data',
    icon: '📊',
    question: '로또 데이터는 어디서 가져오나요?',
    answer: [
      '동행복권 공식 API를 통해 직접 수집합니다.',
      '매일 자동으로 최신 당첨번호를 확인하고 업데이트합니다.',
      '1회차(2002년)부터 최신 회차까지 모든 데이터를 보유하고 있습니다.'
    ]
  },
  {
    id: 10,
    category: 'data',
    icon: '🔄',
    question: '데이터는 얼마나 자주 업데이트되나요?',
    answer: [
      'GitHub Actions를 통해 매일 자동으로 최신 데이터를 확인합니다.',
      '새로운 추첨 결과가 있으면 즉시 반영됩니다.',
      '통상적으로 추첨일(매주 토요일) 저녁 이후 업데이트됩니다.'
    ]
  },

  // 개인정보 관련
  {
    id: 11,
    category: 'privacy',
    icon: '🔒',
    question: '회원가입이 필요한가요?',
    answer: [
      '아니요! 로또 지니어스는 회원가입이 전혀 필요 없습니다.',
      '이름, 전화번호, 이메일 등 어떠한 개인정보도 수집하지 않습니다.',
      '누구나 바로 접속해서 자유롭게 이용할 수 있습니다.'
    ]
  },
  {
    id: 12,
    category: 'privacy',
    icon: '🍪',
    question: '쿠키나 개인정보를 수집하나요?',
    answer: [
      'Google Analytics와 AdSense를 위한 기본적인 쿠키만 사용합니다.',
      '수집되는 정보는 익명화된 통계 데이터(페이지 방문, 체류 시간 등)뿐입니다.',
      '자세한 내용은 개인정보처리방침 페이지에서 확인하실 수 있습니다.'
    ]
  },

  // 기타
  {
    id: 13,
    category: 'service',
    icon: '🐛',
    question: '버그나 오류를 발견했어요. 어디에 알려야 하나요?',
    answer: [
      'GitHub Issues 또는 문의하기 페이지를 통해 알려주세요.',
      '버그 리포트 시 다음 정보를 포함해주시면 빠른 해결에 도움이 됩니다:',
      '- 발생한 오류 메시지\n- 사용 중인 브라우저와 기기\n- 문제가 발생한 상황'
    ]
  },
  {
    id: 14,
    category: 'service',
    icon: '💡',
    question: '새로운 기능을 제안하고 싶어요.',
    answer: [
      '환영합니다! GitHub Issues 또는 문의하기 페이지로 제안해 주세요.',
      '모든 제안을 검토하며, 유용한 기능은 우선순위에 따라 구현합니다.',
      '새로운 AI 캐릭터나 패턴 아이디어도 좋습니다!'
    ]
  },
  {
    id: 15,
    category: 'service',
    icon: '🌍',
    question: '다른 나라 로또도 지원하나요?',
    answer: [
      '현재는 한국 로또 6/45만 지원합니다.',
      '향후 사용자 요청이 많으면 다른 나라의 로또도 추가할 수 있습니다.'
    ]
  }
]);

const filteredFAQs = computed(() => {
  let result = faqs.value;
  
  // 카테고리 필터
  if (selectedCategory.value !== 'all') {
    result = result.filter(faq => faq.category === selectedCategory.value);
  }
  
  // 검색 필터
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(faq =>
      faq.question.toLowerCase().includes(query) ||
      faq.answer.some(a => a.toLowerCase().includes(query))
    );
  }
  
  return result;
});
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 1rem;
}

details summary {
  list-style: none;
}

details summary::-webkit-details-marker {
  display: none;
}

details[open] summary {
  color: #2563eb;
}
</style>
